import axios from "axios";
import { getDateComponents } from "./utils";
import {
	formatTemplateMoney,
	templateMatchesInsuranceCompany,
} from "./claim-workflow";

const API_BASE_URL = process.env.REACT_APP_API_URL;
const BACKEND_APP_API_URL =
	process.env.BACKEND_APP_API_URL ||
	"https://emacs-backend-prd.azurewebsites.net";

if (!API_BASE_URL) {
	console.error("API base URL is not defined. Check environment variables.");
	throw new Error("API configuration error.");
}

export const getInvoice = async (additionalRows, assignmentName, serviceFee) => {
	try {
		const request = {
			assignmentName: assignmentName,
			InvoiceDetails: additionalRows,
			// Omitted rather than sent as null when absent, so the backend falls back to
			// the standard fee schedule instead of billing zero.
			...(Number.isFinite(Number(serviceFee))
				? { serviceFee: Number(serviceFee) }
				: {}),
		};
		const response = await axios.post(
			`${API_BASE_URL}/GetInvoice`,
			request,
		);
		return response.data;
	} catch (error) {
		console.error("Error getting invoice:", error);
		throw error; // Rethrow the error to handle it in the calling function
	}
};

export const uploadBulkAssignments = async (file) => {
	try {
		const formData = new FormData();
		formData.append("file", file);
		const response = await axios.post(
			`${API_BASE_URL}/CreateBulkAssignmentFromFile`,
			formData,
			{
				headers: { "Content-Type": "multipart/form-data" },
			},
		);
		return response.data;
	} catch (error) {
		console.error("Error uploading bulk assignments:", error);
		throw error;
	}
};

export const fetchAssignmentsById = async (assignmentId) => {
	try {
		const assignmentResponse = await axios.get(
			`${API_BASE_URL}/GetAssignments?assignmentName=${assignmentId}`,
		);
		if (assignmentResponse.data.length > 0) {
			return assignmentResponse.data[0];
		} else {
			return null;
		}
	} catch (error) {
		console.error("Error fetching assignment:", error);
		throw error;
	}
};

export function encodeImageToBase64(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const base64String = reader.result.split(",")[1];
			resolve(base64String);
		};
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
}

export const analyseImage = async (image_url) => {
	try {
		if (!image_url) {
			return { error: "Image is required." };
		}

		console.log("Sending image to backend for analysis...");
		const response = await axios.post(
			`${BACKEND_APP_API_URL}/api/analyseImage`,
			{ imageUrl: image_url },
		);

		console.log("Analysis result:", response.data);
		return response.data;
	} catch (error) {
		console.error("Error analyzing image:", error.message);
		return {
			error: "Failed to analyze image after multiple attempts",
			details: error.message,
		};
	} finally {
		console.info("Execution complete");
	}
};

export const getToken = async (channelName, uid, role) => {
	try {
		let tokenType = "publisher";
		const url = `${BACKEND_APP_API_URL}/rtc/${channelName}/${tokenType}/uid/${uid}`;
		console.log("Token URL:", url);
		const response = await axios.get(url);
		console.log("Token response:", response.data);
		// Ensure token exists and is valid
		if (!response.data || !response.data.rtcToken) {
			throw new Error("Invalid token received from API");
		}
		const token = response.data.rtcToken.trim();
		return token;
	} catch (error) {
		console.error("Error fetching Agora token:", error);
		return null; // Ensure the caller knows token retrieval failed
	}
};

export const createUser = async (userData) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/CreateUser`,
			userData,
		);
		return response.data;
	} catch (error) {
		console.error("Error in createUser:", error);
		throw error;
	}
};

export const deleteUser = async (username, force = false) => {
	try {
		const response = await axios.delete(
			`${API_BASE_URL}/DeleteUser?username=${encodeURIComponent(
				username,
			)}${force ? "&force=true" : ""}`,
		);
		return response.data;
	} catch (error) {
		console.error("Error in deleteUser:", error);
		throw error;
	}
};

export const getDecryptedValue = async (encryptedAssignmentName) => {
	try {
		//let request = { "cipherText" : encryptedAssignmentName};
		const response = await axios.get(
			`${API_BASE_URL}/IsValidLink?linkData=${encryptedAssignmentName}`,
		);
		return response.data;
	} catch (error) {
		console.error("Error in getDecryptedValue:", error);
		throw error;
	}
};

export const getInsuranceCompanies = async () => {
	try {
		const response = await axios.get(`${API_BASE_URL}/GetCompany`);
		const companyList = response.data.map((company) => ({
			userName: company.id,
			name: company.name,
		}));
		return companyList;
	} catch (error) {
		console.error("Error in getInsuranceCompanies:", error);
		throw error;
	}
};

export const getCategoryBasedOnInsuranceComapny = async (
	insuranceCompanyName,
) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/GetCategory?CompanyName=${insuranceCompanyName.trim()}`,
		);

		const categoryList = [
			...response.data.map((category) => ({
				categoryId: category.categoryId,
				categoryName: category.categoryName,
				subcategory: category.subcategory,
				rate: category.rate,
				nRate: "",
				qty: "",
				assessment: "",
				customSubCategory: "",
				dep: "",
				adjustmentOnIndemnity: "",
			})),
			{
				categoryId: "30",
				categoryName: "Others",
				subcategory: "",
				rate: "",
				nRate: "",
				qty: "",
				assessment: "",
				customSubCategory: "",
				dep: "",
				adjustmentOnIndemnity: "",
			},
			{
				categoryId: "31",
				categoryName: "Others",
				subcategory: "Others",
				rate: "",
				nRate: "",
				qty: "",
				assessment: "",
				customSubCategory: "",
				dep: "",
				adjustmentOnIndemnity: "",
			},
			{
				categoryId: "32",
				categoryName: "Others",
				subcategory: "Others",
				rate: "",
				nRate: "",
				qty: "",
				assessment: "",
				customSubCategory: "",
				dep: "",
				adjustmentOnIndemnity: "",
			},
			{
				categoryId: "33",
				categoryName: "Others",
				subcategory: "Others",
				rate: "",
				nRate: "",
				qty: "",
				assessment: "",
				customSubCategory: "",
				dep: "",
				adjustmentOnIndemnity: "",
			},
			{
				categoryId: "34",
				categoryName: "Others",
				subcategory: "Others",
				rate: "",
				nRate: "",
				qty: "",
				assessment: "",
				customSubCategory: "",
				dep: "",
				adjustmentOnIndemnity: "",
			},
		];
		return categoryList;
	} catch (error) {
		console.error("Error in getCategoryBasedOnInsuranceComapny:", error);
		throw error;
	}
};

export const fetchSavedLOA = async (assignmentName) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/GetLossTemplate?AssignmentName=${assignmentName}`,
		);
		const categoryList = [
			...response.data.map((category) => ({
				categoryId: category.categoryId,
				categoryName: category.category,
				subcategory: category.subCategory,
				rate: category.rate,
				nRate: category.customRate > 0 ? category.customRate : 0,
				qty: category.quantity,
				assessment:
					category.quantity > 0
						? category.quantity * category.rate
						: "",
				customSubCategory: category.category === "Others" ? category.subCategory : "",
				dep: category.depreciation > 0 ? category.depreciation : "",
				adjustmentOnIndemnity:
					category.quantity > 0
						? category.depreciation === 0
							? category.quantity * category.rate
							: category.quantity * category.rate -
								(category.depreciation / 100) *
									(category.quantity * category.rate)
						: "",
			})),
		];

		return categoryList;
	} catch (error) {
		console.error("Error in fetchSavedLOA : ", error);
		throw error;
	}
};

export const fetchUsers = async (role) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/GetUser?role=${role}`,
		);
		const userList = response.data.map((user) => ({
			userName: user.userName,
			name: user.firstName + " " + user.lastName,
		}));
		return userList;
	} catch (error) {
		console.error("Error in fetchUsers:", error);
		throw error;
	}
};

export const fetchAllUsers = async () => {
	try {
		const response = await axios.get(`${API_BASE_URL}/GetUser`);
		const userList = response.data.map((user) => ({
			userName: user.userName,
			firstName: user.firstName,
			middleName: user.middleName,
			userInitial: user.userInitial,
			lastName: user.lastName,
			email: user.email,
			mobileNumber: user.mobileNumber,
			role: user.role,
		}));
		return userList;
	} catch (error) {
		console.error("Error in fetchAllUsers:", error);
		throw error;
	}
};

export const changePassword = async (
	userName,
	currentPassword,
	newPassword,
) => {
	try {
		const request = {
			userName: userName,
			password: currentPassword,
			newPassword: newPassword,
		};
		const response = await axios.put(
			`${API_BASE_URL}/ChangePassword`,
			request,
		);
		return response.data; // Return the API response
	} catch (error) {
		console.error("Error in changePassword:", error);
		throw error; // Rethrow the error to handle it in the calling function
	}
};

export const loginUser = async (username, password) => {
	try {
		const request = {
			userName: username,
			password: password,
		};
		const response = await axios.post(`${API_BASE_URL}/Login`, request);

		return response.data; // Properly return the response data
	} catch (error) {
		console.error("Error logging in:", error);
		if (error.status === 401) {
			alert("Password is wrong.");
		}
		throw error; // Rethrow the error to handle it in the calling function
	}
};

export const fetchAssignments = async (user) => {
	try {
		let response = null;
		if (user?.role === "Maker" || user?.role === "SysAdmin") {
			// Makers and SysAdmins see all assignments
			response = await axios.get(`${API_BASE_URL}/GetAssignments`);
		} else {
			response = await axios.get(
				`${API_BASE_URL}/GetAssignments?userName=${user?.userName}`,
			);
		}

		return response.data; // Properly return the response data
	} catch (error) {
		console.error("Error logging in:", error);
		throw error; // Rethrow the error to handle it in the calling function
	}
};

export const fetchAttachmentList = async (assignmentName) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/GetAttachmentList?assignmentName=${assignmentName}`,
		);

		if (!response || !response.data || !response.data[0]) {
			// console.warn("No data received from API or data is undefined.");
			return null; // Return null if the response or data is undefined
		}

		return response.data; // Properly return the response data
	} catch (error) {
		console.error("Error logging in:", error);
		throw error; // Rethrow the error to handle it in the calling function
	}
};

export const updateAssignment = async (updatedAssignment, user) => {
	try {
		await axios.put(`${API_BASE_URL}/UpdateAssignment`, updatedAssignment);

		const assignment = await fetchAssignments(user);

		return assignment;
	} catch (error) {
		console.error("Error in updating assignment:", error);
		throw error;
	}
};

// Workflow transitions must not send a stale browser copy of the entire assignment.
// The API updates only STATUS, preserving loss assessment and other independently
// persisted fields.
export const updateAssignmentStatus = async (assignmentName, status, user) => {
	try {
		await axios.put(`${API_BASE_URL}/UpdateAssignmentStatus`, null, {
			params: { assignmentName, status },
		});
		return await fetchAssignments(user);
	} catch (error) {
		console.error("Error in updating assignment status:", error);
		throw error;
	}
};

export const createAssignment = async (assignment) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/CreateAssignment`,
			assignment,
		);
		return response.data;
	} catch (error) {
		console.error("Error in creating assignment:", error);
		throw error;
	}
};

export const uploadAttachment = async (data) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/UploadAttachment`,
			data,
		);
		return response.data;
	} catch (error) {
		console.error("Error in upload attachment to assignment:", error);
		throw error;
	}
};

export const createClaimant = async (dataList) => {
	const requiredFields = [
		"id",
		"partyId",
		"assignmentName",
		"insuredName",
		"insuredContactDetails",
		"isPrimary",
		"createdBy",
		"createdDatetime",
		"lastUpdatedDatetime",
		"lastUpdatedBy",
	];

	if (!Array.isArray(dataList)) {
		throw new Error("Expected an array of claimants");
	}

	const results = [];

	for (const [index, data] of dataList.entries()) {
		for (const field of requiredFields) {
			if (data[field] === undefined || data[field] === null) {
				throw new Error(
					`Missing required field: ${field} in claimant at index ${index}`,
				);
			}
		}

		try {
			const response = await fetch(`${API_BASE_URL}/CreateClaimant`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(data),
			});

			if (!response.ok)
				throw new Error(
					`CreateClaimant failed (index ${index}) with status ${response.status}`,
				);
			const result = await response.json();
			results.push(result);
		} catch (error) {
			console.error(`createClaimant error at index ${index}:`, error);
			throw error; // Optionally continue instead of throw
		}
	}

	return results;
};

export const getClaimant = async (id) => {
	if (!id || typeof id !== "number") {
		throw new Error("Invalid or missing ID");
	}

	try {
		const response = await fetch(`${API_BASE_URL}/GetClaimant?id=${id}`);

		if (!response.ok)
			throw new Error(
				`GetClaimant failed with status ${response.status}`,
			);
		return await response.json();
	} catch (error) {
		console.error("getClaimant error:", error);
		throw error;
	}
};

export const updateClaimant = async (dataList) => {
	const requiredFields = [
		"id",
		"assignmentName",
		"insuredName",
		"insuredContactDetails",
		"isPrimary",
		"createdBy",
		"createdDatetime",
		"lastUpdatedDatetime",
		"lastUpdatedBy",
	];

	if (!Array.isArray(dataList)) {
		throw new Error("Expected an array of claimants");
	}

	const results = [];

	for (const [index, data] of dataList.entries()) {
		if (!data.id || typeof data.id !== "number") {
			throw new Error(
				`Invalid or missing ID in claimant at index ${index}`,
			);
		}

		for (const field of requiredFields) {
			if (data[field] === undefined || data[field] === null) {
				throw new Error(
					`Missing required field: ${field} in claimant at index ${index}`,
				);
			}
		}

		try {
			const response = await fetch(`${API_BASE_URL}/UpdateClaimant`, {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(data),
			});

			if (!response.ok)
				throw new Error(
					`UpdateClaimant failed (index ${index}) with status ${response.status}`,
				);
			const result = await response.json();
			results.push(result);
		} catch (error) {
			console.error(`updateClaimant error at index ${index}:`, error);
			throw error; // Optionally continue instead of throw
		}
	}

	return results;
};

export const deleteClaimant = async (idList, assignmentName) => {
	if (!Array.isArray(idList)) {
		throw new Error("Expected an array of claimant IDs.");
	}

	const results = [];

	for (const [index, id] of idList.entries()) {
		if (typeof id !== "number" || isNaN(id)) {
			throw new Error(`Invalid ID at index ${index}`);
		}

		try {
			const response = await fetch(
				`${API_BASE_URL}/DeleteClaimant?id=${id}&AssignmentName=${assignmentName}`,
				{
					method: "DELETE",
				},
			);

			if (!response.ok)
				throw new Error(
					`DeleteClaimant failed for ID ${id} with status ${response.status}`,
				);
			const result = await response.json();
			results.push(result);
		} catch (error) {
			console.error(
				`deleteClaimant error for ID ${id} at index ${index}:`,
				error,
			);
			throw error; // Or continue and collect errors if needed
		}
	}

	return results;
};

export const uploadSignature = async (data) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/UploadSignature`,
			data,
		);
		return response.data;
	} catch (error) {
		console.error("Error in upload signature:", error);
		throw error;
	}
};

export const uploadSnapshots = async (request, user) => {
	try {
		// Check if we need to analyze any snapshots before uploading
		// This is tricky since request is a FormData object
		// If there's attachmentDetails in the FormData, we can parse and potentially analyze it
		const attachmentDetailsStr = request.get("attachmentDetails");
		if (attachmentDetailsStr) {
			try {
				// Parse the attachment details
				const attachmentDetails = JSON.parse(attachmentDetailsStr);

				// Check if any snapshots need analysis
				const needsAnalysis = attachmentDetails.some(
					(detail) =>
						!detail.customDescription ||
						!detail.height ||
						detail.height === 0 ||
						!detail.width ||
						detail.width === 0 ||
						!detail.description,
				);

				// If analysis is needed, we'll need to reconstruct the request with analyzed data
				// This is complex because we'd need to match the image data in the form
				// For now, we'll proceed with the upload as is
				if (needsAnalysis) {
					console.info(
						"Some snapshots may benefit from AI analysis. Consider using analyzeSnapshotIfNeeded before uploading.",
					);
				}
			} catch (parseError) {
				console.warn(
					"Could not parse attachment details for analysis:",
					parseError,
				);
			}
		}

		// Proceed with the upload
		await axios.post(`${API_BASE_URL}/UploadSnapshot`, request);

		return 1;
	} catch (error) {
		console.error("Error in upload snapshots to assignment:", error);
		throw new Error(
			error.response?.data?.message ||
				error.response?.data?.Message ||
				error.message ||
				"Failed to upload the loss assessment.",
		);
	}
};

// Fallback: Save Loss Assessment directly without snapshots/opener
// Reuses the same backend endpoint as snapshot upload, but sends only LOA fields.
export const saveLossAssessment = async (
	payload /* { assignmentName, settledAmount, sumInsured, totalArea, perSqFtValue, excessValue, losstemplate, summaryDescriptions } */,
	companyName = "",
) => {
	try {
		if (!payload || !payload.assignmentName) {
			throw new Error(
				"assignmentName is required to save loss assessment",
			);
		}

		// Build a FormData matching the shape used by handleLossSubmit
		const request = new FormData();
		Object.entries(payload).forEach(([key, value]) => {
			// Ensure primitives/strings are appended as-is
			request.append(key, value);
		});
		// No images in fallback; include empty attachmentDetails and company
		request.append("attachmentDetails", JSON.stringify([]));
		request.append("companyName", companyName || "");

		// Delegate to existing uploadSnapshots API to leverage backend persistence path
		await uploadSnapshots(request, null);
		return 1;
	} catch (error) {
		console.error("Error saving loss assessment directly:", error);
		throw error;
	}
};

export const sendMessage = async (assignmentName, type, party_id) => {
	try {
		await axios.post(
			`${API_BASE_URL}/SendClaimNotification?AssignmentName=${encodeURIComponent(
				assignmentName,
			)}&NotificationType=${encodeURIComponent(
				type,
			)}&PartyId=${encodeURIComponent(party_id)}`,
		);
	} catch (error) {
		console.error("Error in sending message:", error);
		throw error;
	}
};

export const updateDescription = async (
	assignmentName,
	snapshot,
	new_description,
) => {
	try {
		// Extract the exact filename from the snapshot URL
		const urlParams = new URLSearchParams(
			new URL(snapshot.imageData).search,
		);
		const fileName = urlParams.get("attachmentName");

		if (!fileName) {
			throw new Error("Could not extract filename from snapshot URL");
		}

		// Analyze snapshot if any fields are empty
		let snapshotToUpdate = snapshot;
		if (
			!snapshot.customDescription ||
			!snapshot.height ||
			snapshot.height === 0 ||
			!snapshot.width ||
			snapshot.width === 0 ||
			!new_description
		) {
			// Use image analysis to fill in missing fields
			snapshotToUpdate = await analyzeSnapshotIfNeeded(snapshot);
		}

		const response = await axios.put(
			`${API_BASE_URL}/UpdateAttachment?assignmentName=${assignmentName}`,
			{
				fileName: fileName,
				description: new_description || snapshotToUpdate.description,
				customDescription: snapshotToUpdate.customDescription,
				height: snapshotToUpdate.height,
				width: snapshotToUpdate.width,
			},
			{
				headers: {
					"Content-Type": "application/json",
				},
			},
		);

		if (!response.data) {
			throw new Error("Failed to update description");
		}

		return response.data;
	} catch (error) {
		console.error("Error updating description:", error);
		throw error;
	}
};

export const deleteAttachment = async (assignmentName, fileName) => {
	try {
		const response = await axios.delete(
			`${API_BASE_URL}/DeleteAttachment?assignmentName=${assignmentName}&attachmentName=${fileName}`,
		);
		return response.data;
	} catch (error) {
		console.error("Error in deleting attachment:", error);
		throw error;
	}
};

export const fetchFormHtml = async (templateId, assignmentName) => {
	try {
		console.log(
			`Fetching form HTML for template: ${templateId}, assignment: ${assignmentName}`,
		);
		const response = await axios.get(
			`${BACKEND_APP_API_URL}/api/form/${templateId}/${encodeURIComponent(
				assignmentName,
			)}`,
		);

		console.log("Form HTML API response:", response.status);
		if (response.data && response.data.html) {
			console.log("HTML content length:", response.data.html.length);
		} else {
			console.warn("API returned no HTML content in response");
		}

		return response.data;
	} catch (error) {
		console.error("Error fetching form HTML:", error);
		throw error;
	}
};

export const saveFormData = async (assignmentName, formData, html) => {
	const response = await axios.post(
		`${BACKEND_APP_API_URL}/api/saveFormData/${encodeURIComponent(
			assignmentName,
		)}`,
		{
			formData,
			html,
		},
	);
	return response.data;
};

export const submitSignature = async (assignmentName, claimantData, html) => {
	const response = await axios.post(
		`${BACKEND_APP_API_URL}/api/submitSignature/${encodeURIComponent(
			assignmentName,
		)}`,
		{
			claimantData,
			html,
		},
	);
	return response.data;
};

export const deletePartialFiles = async (assignmentName) => {
	const response = await axios.delete(
		`${BACKEND_APP_API_URL}/api/deletePartialFiles/${encodeURIComponent(
			assignmentName,
		)}`,
	);
	return response.data;
};

export const getAnalytics = async (reportName, params = {}) => {
	const query = new URLSearchParams({ ReportType: reportName });
	if (params.month) {
		query.append("month", params.month);
	}
	if (params.year) {
		query.append("year", params.year);
	}
	const response = await axios.get(
		`${API_BASE_URL}/AnalyticsReport?${query.toString()}`,
	);
	return response.data;
};

// --- OpenSign Integration Functions ---
// NOTE: These OpenSign functions are deprecated and maintained only for backward compatibility.
// Please use the new implementation in opensign-role-support.js for all new code.
export const OPENSIGN_BASE_URL = process.env.REACT_APP_OPENSIGN_API_URL; // e.g., https://example.com/app
const OPENSIGN_APP_ID = process.env.REACT_APP_OPENSIGN_APP_ID;
const OPENSIGN_INSTALLATION_ID =
	process.env.REACT_APP_OPENSIGN_INSTALLATION_ID ||
	"51cf9a90-a1e2-47fe-accf-2bef6d2af3e3"; // Default installation ID
const OPENSIGN_API_USER = process.env.REACT_APP_OPENSIGN_USER;
const OPENSIGN_API_PASSWORD = process.env.REACT_APP_OPENSIGN_PASSWORD;
const OPENSIGN_MASTER_KEY = process.env.REACT_APP_OPENSIGN_MASTER_KEY;

let openSignSessionToken = null;

export const loginOpenSign = async () => {
	// Check if a valid token exists and is not expired
	// (Assuming a simple check here, you might want to add actual expiry logic if needed)
	if (openSignSessionToken) {
		// Potentially add a check here to see if the token is expired if you store an expiry time
		// For now, we assume if it exists, it might still be valid, getOpenSignAuthHeaders will trigger re-login if not.
		return openSignSessionToken;
	}

	if (
		!OPENSIGN_BASE_URL ||
		!OPENSIGN_APP_ID ||
		!OPENSIGN_API_USER ||
		!OPENSIGN_API_PASSWORD
	) {
		console.error(
			"OpenSign environment variables for authentication are not fully configured.",
		);
		// throw new Error("OpenSign authentication configuration error."); // Avoid throwing here to allow UI to handle
		return null; // Indicate failure
	}
	try {
		const response = await axios.post(
			`${OPENSIGN_BASE_URL}/functions/loginuser`,
			{
				email: OPENSIGN_API_USER,
				password: OPENSIGN_API_PASSWORD,
				_ApplicationId: OPENSIGN_APP_ID,
				_InstallationId: OPENSIGN_INSTALLATION_ID,
			},
			{
				headers: {
					"Content-Type": "application/json",
					"X-Parse-Application-Id": OPENSIGN_APP_ID,
					"X-Parse-Master-Key": OPENSIGN_MASTER_KEY, // Add Master Key for authentication
					// Dont use 'Origin' and 'Referer' headers as browsers block them for security reasons
				},
			},
		);
		if (
			response.data &&
			response.data.result &&
			response.data.result.sessionToken
		) {
			openSignSessionToken = response.data.result.sessionToken;
			localStorage.setItem("openSignSessionToken", openSignSessionToken);
			// If you had expiry: localStorage.setItem('openSignTokenExpiry', calculate_expiry_time);
			console.info("OpenSign login successful.");
			return openSignSessionToken;
		} else {
			console.error(
				"OpenSign login failed: Invalid response structure from /loginuser",
				response.data,
			);
			openSignSessionToken = null; // Clear any potentially stale token
			localStorage.removeItem("openSignSessionToken");
			// throw new Error("OpenSign login failed - invalid response.");
			return null; // Indicate failure
		}
	} catch (error) {
		console.error(
			"Error during OpenSign login:",
			error.response ? error.response.data : error.message,
		);
		openSignSessionToken = null; // Clear token on error
		localStorage.removeItem("openSignSessionToken");
		// throw error;
		return null; // Indicate failure
	}
};

export const getOpenSignAuthHeaders = async () => {
	if (!OPENSIGN_APP_ID) {
		console.error("OpenSign App ID is not configured.");
		// throw new Error("OpenSign App ID configuration error.");
		return null; // Indicate failure
	}

	let currentToken = openSignSessionToken;

	if (!currentToken) {
		console.log("No OpenSign session token found, attempting to login...");
		currentToken = await loginOpenSign();
	}

	if (!currentToken) {
		console.error(
			"OpenSign session token not available after login attempt.",
		);
		// throw new Error("OpenSign session token not available after login attempt.");
		return null; // Indicate failure
	}
	openSignSessionToken = currentToken; // Ensure module-level variable is also up-to-date
	return {
		"Content-Type": "application/json",
		"X-Parse-Application-Id": OPENSIGN_APP_ID,
		"X-Parse-Session-Token": currentToken, // Using X-Parse-Session-Token as per documentation
		Sessiontoken: currentToken, // Keep Sessiontoken for backward compatibility
		"X-Parse-Master-Key": OPENSIGN_MASTER_KEY, // Add Master Key for elevated permissions
		// Removed 'Origin' and 'Referer' headers as browsers block them for security reasons
		// Removed 'Authorization' header as we're already using X-Parse-Session-Token
	};
};

export const fetchOpenSignTemplates = async (retryAttempt = 0) => {
	try {
		const headers = await getOpenSignAuthHeaders();
		if (!headers) {
			console.error(
				"OpenSign authentication failed or headers not available, cannot fetch templates.",
			);
			return [];
		}

		const reportIdForUserTemplates = "6TeaPr321t"; // For "All Templates (Created by user)"
		// const reportIdForSharedTemplates = "t8e7M6p5L4k"; // For "All Templates (Shared with user)"
		// Choose or combine as needed.

		const response = await axios.post(
			`${OPENSIGN_BASE_URL}/functions/getReport`,
			{ reportId: reportIdForUserTemplates },
			{ headers },
		);

		if (response.data && Array.isArray(response.data.result)) {
			return response.data.result.map((template) => ({
				id: template.objectId,
				name: template.Name,
			}));
		} else {
			console.error(
				"Unexpected response structure for OpenSign templates (/getReport):",
				response.data,
			);
			return [];
		}
	} catch (error) {
		console.error(
			"Error fetching OpenSign templates:",
			error.response ? error.response.data : error.message,
		);

		// Retry logic for token expiry
		if (
			error.response &&
			(error.response.status === 401 || error.response.status === 403) &&
			retryAttempt < 1
		) {
			const errorData = error.response.data;
			if (
				errorData &&
				(errorData.error === "Invalid session token" ||
					errorData.code === 209 ||
					errorData.code === 141)
			) {
				console.log(
					"OpenSign session token invalid during fetchOpenSignTemplates, attempting to re-login...",
				);
				openSignSessionToken = null;
				localStorage.removeItem("openSignSessionToken");
				const newAuthToken = await loginOpenSign();
				if (newAuthToken) {
					return fetchOpenSignTemplates(retryAttempt + 1);
				}
			}
		}

		return [];
	}
};

/**
 * Fetches the template details including role definitions
 * @param {string} templateId - The ID of the template to fetch
 * @returns {Promise<Object>} Template details including roles
 */
export const getOpenSignTemplate = async (templateId, retryAttempt = 0) => {
	if (!OPENSIGN_BASE_URL) {
		console.error("OpenSign API base URL is not defined.");
		throw new Error("OpenSign API configuration error.");
	}

	if (!templateId) {
		console.error("No template ID provided");
		throw new Error("Template ID is required");
	}

	try {
		const headers = await getOpenSignAuthHeaders();
		if (!headers) {
			console.error(
				"OpenSign authentication failed, cannot fetch template details.",
			);
			throw new Error("OpenSign authentication failed.");
		}

		console.log(`Fetching template details for template ID: ${templateId}`);

		const response = await axios.post(
			`${OPENSIGN_BASE_URL}/functions/getTemplate`,
			{ templateId: templateId.trim() },
			{ headers },
		);

		if (!response.data || !response.data.result) {
			console.error(
				"Invalid response structure from getTemplate:",
				response.data,
			);
			throw new Error("Failed to fetch template details");
		}

		console.log("Template details retrieved:", response.data.result);
		return response.data.result;
	} catch (error) {
		console.error(
			"Error fetching template details:",
			error.response ? error.response.data : error.message,
		);

		// Retry logic for token expiry
		if (
			error.response &&
			(error.response.status === 401 || error.response.status === 403) &&
			retryAttempt < 1
		) {
			const errorData = error.response.data;
			if (
				errorData &&
				(errorData.error === "Invalid session token" ||
					errorData.code === 209 ||
					errorData.code === 141)
			) {
				console.log(
					"OpenSign session token invalid during getOpenSignTemplate, attempting to re-login...",
				);
				openSignSessionToken = null;
				localStorage.removeItem("openSignSessionToken");
				const newAuthToken = await loginOpenSign();
				if (newAuthToken) {
					return getOpenSignTemplate(templateId, retryAttempt + 1);
				}
			}
		}

		throw error;
	}
};

export const checkDocumentExists = async (documentName, retryAttempt = 0) => {
	try {
		const headers = await getOpenSignAuthHeaders();
		if (!headers) {
			console.error(
				"OpenSign authentication failed, cannot check document existence.",
			);
			return null;
		}

		// Search across multiple OpenSign report types to find documents in different states
		const reportIdsToSearch = [
			"ByHuevtCFY", // Draft Documents
			"1MwEuxLEkF", // In-progress
			"kQUoW4hUXz", // Completed
			"4Hhwbp482K", // Need your sign
		];

		console.log(`Checking if document exists: "${documentName}"`);

		// Search through each report type until we find the document
		for (const reportId of reportIdsToSearch) {
			try {
				console.log(`Searching in report type: ${reportId}`);

				const response = await axios.post(
					`${OPENSIGN_BASE_URL}/functions/getReport`,
					{
						reportId: reportId,
						limit: 100, // Get a reasonable number of documents to search through
					},
					{ headers },
				);

				if (response.data && Array.isArray(response.data.result)) {
					// Search for a document with the exact name
					const existingDocument = response.data.result.find(
						(doc) =>
							doc.Name && doc.Name.trim() === documentName.trim(),
					);

					if (existingDocument) {
						console.log(
							`Document "${documentName}" found in OpenSign report ${reportId}`,
						);
						console.log("Found existing document:", {
							objectId: existingDocument.objectId,
							name: existingDocument.Name,
							createdAt: existingDocument.createdAt,
							reportType: reportId,
						});

						// Return the full document object so the caller can access objectId
						return existingDocument;
					}
				} else {
					console.warn(
						`Unexpected response structure for report ${reportId}:`,
						response.data,
					);
				}
			} catch (reportError) {
				console.warn(
					`Error searching report ${reportId}:`,
					reportError.message,
				);
				// Continue to next report type
			}
		}

		console.log(
			`Document "${documentName}" not found in any OpenSign reports`,
		);
		return null;
	} catch (error) {
		console.error(
			"Error checking document existence:",
			error.response ? error.response.data : error.message,
		);

		// Retry logic for token expiry
		if (
			error.response &&
			(error.response.status === 401 || error.response.status === 403) &&
			retryAttempt < 1
		) {
			const errorData = error.response.data;
			if (
				errorData &&
				(errorData.error === "Invalid session token" ||
					errorData.code === 209 ||
					errorData.code === 141)
			) {
				console.log(
					"OpenSign session token invalid during document existence check, attempting to re-login...",
				);
				openSignSessionToken = null;
				localStorage.removeItem("openSignSessionToken");
				const newAuthToken = await loginOpenSign();
				if (newAuthToken) {
					return checkDocumentExists(documentName, retryAttempt + 1);
				}
			}
		}

		return null;
	}
};

/**
 * Analyzes a snapshot image if any of the important fields are missing or empty.
 * This function uses AI image analysis (via analyseImage) to fill in details like:
 * - customDescription (object detected)
 * - height and width dimensions (in feet)
 * - description (damage assessment)
 *
 * @param {Object} snapshot - The snapshot object with at minimum an imageData property
 * @returns {Object} - An enhanced snapshot with all fields filled where possible
 */
export const analyzeSnapshotIfNeeded = async (snapshot) => {
	// Check if any of the required fields are empty or have default values
	const needsAnalysis =
		!snapshot.customDescription ||
		!snapshot.height ||
		snapshot.height === 0 ||
		!snapshot.width ||
		snapshot.width === 0 ||
		!snapshot.description ||
		snapshot.description === "No description available";

	// If all fields are already populated, return the snapshot as is
	if (!needsAnalysis) {
		return snapshot;
	}

	try {
		// Call analyseImage with the snapshot's image data
		const analysisResult = await analyseImage(snapshot.imageData);

		// If there was an error in analysis, log it and return an error object
		if (analysisResult.error) {
			console.warn(
				"Image analysis error:",
				analysisResult.error,
				analysisResult.details,
			);
			return {
				...snapshot,
				error: analysisResult.error,
				details: analysisResult.details,
			};
		}

		// Return a new object with the analyzed values, keeping existing values if they're valid
		return {
			...snapshot,
			customDescription:
				snapshot.customDescription ||
				analysisResult.customDescription ||
				"Unknown",
			height:
				snapshot.height && snapshot.height > 0
					? snapshot.height
					: analysisResult.height || 0,
			width:
				snapshot.width && snapshot.width > 0
					? snapshot.width
					: analysisResult.width || 0,
			description:
				snapshot.description ||
				analysisResult.description ||
				"No description available",
		};
	} catch (error) {
		console.error("Error analyzing snapshot:", error);
		// Return original snapshot if analysis fails
		return snapshot;
	}
};

/**
 * Batch analyzes multiple snapshots in parallel to improve efficiency.
 * This is useful when processing a collection of snapshots that might be missing
 * important metadata like dimensions or descriptions.
 *
 * @param {Array} snapshots - Array of snapshot objects to analyze
 * @returns {Array} - Enhanced snapshots with all fields filled where possible
 */
export const batchAnalyzeSnapshots = async (snapshots) => {
	if (!snapshots || !Array.isArray(snapshots)) {
		return [];
	}

	// Process all snapshots in parallel for better performance
	const analysisPromises = snapshots.map((snapshot) =>
		analyzeSnapshotIfNeeded(snapshot),
	);

	try {
		// Wait for all analyses to complete
		const analyzedSnapshots = await Promise.all(analysisPromises);
		return analyzedSnapshots;
	} catch (error) {
		console.error("Error in batch snapshot analysis:", error);
		// Return original snapshots if batch analysis fails
		return snapshots;
	}
};

export const createDocumentFromTemplate = async (
	templateId,
	documentName,
	metadata = {},
) => {
	try {
		const headers = await getOpenSignAuthHeaders();
		if (!headers) {
			throw new Error("OpenSign authentication failed");
		}

		if (typeof documentName !== "string") {
			throw new Error("Document name must be a string");
		}

		// First, get the template details
		const templateDetails = await getOpenSignTemplate(templateId);
		if (!templateDetails) {
			throw new Error("Template not found or could not be retrieved");
		}

		if (
			metadata.insuranceCompany &&
			!templateMatchesInsuranceCompany(
				templateDetails.Name || templateDetails.name || "",
				metadata.insuranceCompany,
			)
		) {
			const templateName =
				templateDetails.Name || templateDetails.name || "Unknown template";
			throw new Error(
				`The selected template (${templateName}) does not match ${metadata.insuranceCompany}.`,
			);
		}

		// Process placeholders and signers - this is crucial for proper role assignment
		let processedPlaceholders = [];
		let processedSigners = [];
		let hasUnassignedRoles = false;

		if (
			templateDetails.Placeholders &&
			templateDetails.Placeholders.length > 0
		) {
			// Filter out prefill placeholders
			const filteredPlaceholders = templateDetails.Placeholders.filter(
				(placeholder) => placeholder.Role !== "prefill",
			);

			// Process each placeholder
			processedPlaceholders = filteredPlaceholders.map((placeholder) => {
				// Create a copy of the placeholder without modifying the original
				const processedPlaceholder = {
					...placeholder,
				};

				// Check if placeHolder array exists and has elements
				if (
					processedPlaceholder.placeHolder &&
					processedPlaceholder.placeHolder.length > 0
				) {
					// Update each placeholder position with data from assignment
					processedPlaceholder.placeHolder =
						processedPlaceholder.placeHolder.map((ph) => {
							// Create a copy of the placeholder position
							const updatedPh = { ...ph };

							// If pos array exists, update options with default values from assignment data
							if (updatedPh.pos && updatedPh.pos.length > 0) {
								updatedPh.pos = updatedPh.pos.map((pos) => {
									// Create a copy of the position
									const updatedPos = { ...pos };

									const { day, month, year } =
										getDateComponents(new Date());

									// Only update if options exists and has a name property
									if (
										updatedPos.options &&
										updatedPos.options.name
									) {
										const fieldName =
											updatedPos.options.name;

										// Check if metadata contains assignment data
										if (metadata) {
											// Set default values based on field name
											switch (fieldName) {
												case "address":
													if (metadata.address) {
														updatedPos.options.defaultValue =
															metadata.address;
													}
													break;
												case "settledAmount":
													if (
														metadata.settledAmount !==
															null &&
														metadata.settledAmount !==
															undefined &&
														metadata.settledAmount !== ""
													) {
														updatedPos.options.defaultValue =
															formatTemplateMoney(
																metadata.settledAmount,
															);
													}
													break;
												case "settledAmountInWords":
													if (
														metadata.settledAmountInWords
													) {
														updatedPos.options.defaultValue =
															metadata.settledAmountInWords;
													}
													break;
												case "policyNumber":
													if (metadata.policyNumber) {
														updatedPos.options.defaultValue =
															metadata.policyNumber;
													}
													break;
												case "claimNumber":
													if (metadata.claimNumber) {
														updatedPos.options.defaultValue =
															metadata.claimNumber;
													}
													break;
												case "natureOfDamage":
													if (metadata.natureOfLoss) {
														updatedPos.options.defaultValue =
															metadata.natureOfLoss;
													}
													break;
												case "situationOfLoss":
													if (
														metadata.situationOfLoss
													) {
														updatedPos.options.defaultValue =
															metadata.situationOfLoss;
													}
													break;
												case "dateOfDamage":
													if (metadata.dateOfLoss) {
														// Format date as dd-MM-yyyy
														try {
															const date =
																new Date(
																	metadata.dateOfLoss,
																);
															if (!isNaN(date)) {
																const day =
																	String(
																		date.getDate(),
																	).padStart(
																		2,
																		"0",
																	);
																const month =
																	String(
																		date.getMonth() +
																			1,
																	).padStart(
																		2,
																		"0",
																	);
																const year =
																	date.getFullYear();
																updatedPos.options.defaultValue = `${day}-${month}-${year}`;
															}
														} catch (error) {
															console.warn(
																`Error formatting date: ${metadata.dateOfLoss}`,
																error,
															);
														}
													}
													break;
												case "claimantName":
													if (
														metadata.claimants &&
														metadata.claimants
															.length > 0
													) {
														// Find primary claimant first
														const primaryClaimant =
															metadata.claimants.find(
																(c) =>
																	c.isPrimary,
															);
														if (
															primaryClaimant &&
															primaryClaimant.insuredName
														) {
															updatedPos.options.defaultValue =
																primaryClaimant.insuredName;
														} else if (
															metadata
																.claimants[0]
																.insuredName
														) {
															// Fall back to first claimant if no primary is specified
															updatedPos.options.defaultValue =
																metadata.claimants[0].insuredName;
														}
													}
													break;
												case "adjustorName":
													if (
														metadata.adjuster &&
														metadata.adjuster
															.firstName &&
														metadata.adjuster
															.lastName
													) {
														updatedPos.options.defaultValue =
															metadata.adjuster
																.firstName +
															" " +
															metadata.adjuster
																.lastName;
													}
													break;
												case "adjusterName":
													if (
														metadata.adjuster &&
														metadata.adjuster
															.firstName &&
														metadata.adjuster
															.lastName
													) {
														updatedPos.options.defaultValue =
															metadata.adjuster
																.firstName +
															" " +
															metadata.adjuster
																.lastName;
													}
													break;
												case "SignDay":
													// Ensure a plain string, not an object
													updatedPos.options.defaultValue =
														String(day);
													break;
												case "SignMonth":
													updatedPos.options.defaultValue =
														String(month);
													break;

												case "SignYear":
													updatedPos.options.defaultValue =
														String(year);
													break;
												case "adjusterAddress":
													updatedPos.options.defaultValue =
														metadata.adjusterAddress ||
														"MALAYAN ADJUSTMENT COMPANY SDN BHD (3616-W) Unit A-5-1, Block A, Megan Avenue 1, No. 189, Jalan Tun Razak, 50400 Kuala Lumpur, Malaysia.";
													break;

												default:
													// No matching field
													break;
											}
										}
									}
									return updatedPos;
								});
							}
							return updatedPh;
						});
				}

				// Build Signers as full objects (not bare Pointers) so OpenSign
				// can read Name/Email for the "Yet to sign" panel display.
				// Use signerPtr (the full Contactbook object) if available,
				// otherwise fall back to a Pointer with Role as Name.
				const resolvedSignerObjId =
					placeholder.signerObjId ||
					(placeholder.signerPtr &&
						typeof placeholder.signerPtr === "object" &&
						placeholder.signerPtr.objectId) ||
					null;

				if (resolvedSignerObjId) {
					const signerExists = processedSigners.find(
						(s) => s.objectId === resolvedSignerObjId,
					);
					if (!signerExists) {
						if (
							placeholder.signerPtr &&
							typeof placeholder.signerPtr === "object"
						) {
							// Preserve the contact identity but use the template role as the
							// signer name. This keeps Claimant (A) and Claimant (B)
							// distinguishable even when contact display names are generic.
							processedSigners.push({
								...placeholder.signerPtr,
								Name:
									placeholder.Role ||
									placeholder.signerPtr.Name ||
									"",
							});
						} else {
							// Fallback: create a minimal object with Role as Name
							processedSigners.push({
								__type: "Object",
								className: "contracts_Contactbook",
								objectId: resolvedSignerObjId,
								Name: placeholder.Role || "",
								Email: "",
							});
						}
					}
				} else {
					// This role doesn't have an assigned signer
					hasUnassignedRoles = true;
				}

				return processedPlaceholder;
			});
		}

		// Fallback: if processedSigners is still empty after processing placeholders,
		// use the template's own Signers array (it may have the signer contacts)
		if (
			processedSigners.length === 0 &&
			templateDetails.Signers &&
			templateDetails.Signers.length > 0
		) {
			console.log(
				"processedSigners was empty after placeholder processing, using template Signers array",
			);
			processedSigners = templateDetails.Signers.map((signer) => {
				if (signer && typeof signer === "object") {
					// If it's a Pointer, convert to a full object with Name from Roles
					if (signer.__type === "Pointer" && signer.objectId) {
						return {
							__type: "Object",
							className:
								signer.className || "contracts_Contactbook",
							objectId: signer.objectId,
							Name: signer.Name || "",
							Email: signer.Email || "",
						};
					}
					return signer;
				}
				return signer;
			});
		}

		// Final safety: log a warning if Signers is still empty — the document may not work correctly
		if (processedSigners.length === 0 && processedPlaceholders.length > 0) {
			console.warn(
				"WARNING: No signers could be resolved from template placeholders or Signers array. " +
					"The document will be created without signers, which may cause signing issues. " +
					"Template Placeholders:",
				JSON.stringify(
					processedPlaceholders.map((p) => ({
						Role: p.Role,
						signerObjId: p.signerObjId,
						signerPtr: p.signerPtr,
					})),
					null,
					2,
				),
			);
		}

		// Add console log for debugging placeholder values
		console.log(
			"Document created with placeholders:",
			JSON.stringify(processedPlaceholders, null, 2),
		);
		console.log(
			"Document created with signers:",
			JSON.stringify(
				processedSigners.map((s) => ({
					objectId: s.objectId,
					Name: s.Name,
				})),
				null,
				2,
			),
		);

		// Prepare document data based on template
		const documentData = {
			Name: documentName.trim(),
			URL: templateDetails.URL,
			// SignedUrl is document completion state. Copying it from the
			// template makes a new document appear completed after the first
			// participant signs, blocking the remaining claimant recipients.
			SentToOthers: true,
			Status: "active",
			Stage: "review",
			Type: "document",
			Description: templateDetails.Description || "",
			Note: templateDetails.Note || "",
			Placeholders: processedPlaceholders,
			ExtUserPtr: templateDetails.ExtUserPtr,
			CreatedBy: templateDetails.CreatedBy,
			Signers: processedSigners,
			SendinOrder: templateDetails.SendinOrder || false,
			AutomaticReminders: templateDetails.AutomaticReminders || false,
			RemindOnceInEvery: parseInt(templateDetails.RemindOnceInEvery || 5),
			IsEnableOTP: templateDetails.IsEnableOTP || false,
			IsTourEnabled: templateDetails.IsTourEnabled || false,
			AllowModifications: templateDetails.AllowModifications || false,
			TimeToCompleteDays:
				parseInt(templateDetails.TimeToCompleteDays) || 15,
			Metadata: metadata, // Store additional metadata
		};

		// SignatureType: use template's if it has enabled types, otherwise default to all enabled
		const defaultSignatureTypes = [
			{ name: "draw", enabled: true },
			{ name: "typed", enabled: true },
			{ name: "upload", enabled: true },
			{ name: "default", enabled: true },
		];
		const templateSignTypes = templateDetails.SignatureType;
		if (
			Array.isArray(templateSignTypes) &&
			templateSignTypes.length > 0 &&
			templateSignTypes.some((t) => t.enabled)
		) {
			documentData.SignatureType = templateSignTypes;
		} else {
			documentData.SignatureType = defaultSignatureTypes;
		}
		if (templateDetails.NotifyOnSignatures !== undefined) {
			documentData.NotifyOnSignatures =
				templateDetails.NotifyOnSignatures;
		}
		if (templateDetails.Bcc && templateDetails.Bcc.length > 0) {
			documentData.Bcc = templateDetails.Bcc;
		}
		if (templateDetails.RedirectUrl) {
			documentData.RedirectUrl = templateDetails.RedirectUrl;
		}
		if (templateDetails.objectId) {
			documentData.TemplateId = {
				__type: "Pointer",
				className: "contracts_Template",
				objectId: templateDetails.objectId,
			};
		}

		// Add ExpiryDate
		const expiryDate = new Date();
		expiryDate.setDate(expiryDate.getDate() + 30);
		documentData.ExpiryDate = {
			__type: "Date",
			iso: expiryDate.toISOString(),
		}; // Always use POST to create a document with a random ID
		const response = await axios.post(
			`${OPENSIGN_BASE_URL}/classes/contracts_Document`,
			documentData,
			{ headers },
		);

		if (!response.data) {
			throw new Error("Failed to create document from template");
		}

		const documentId = response.data.objectId;

		// Return the document with OpenSign-compatible structure
		return {
			documentId: documentId, // Use documentId consistently
			id: documentId,
			objectId: documentId,
			...response.data,
			status: hasUnassignedRoles ? "needs-signers" : "ready-for-signing",
			hasUnassignedRoles,
			totalRoles: processedPlaceholders.length,
			assignedRoles: processedPlaceholders.filter((p) => p.signerObjId)
				.length,
		};
	} catch (error) {
		console.error(
			"Error creating document:",
			error.response ? error.response.data : error,
		);
		throw error;
	}
};

export const getEmbeddedDocumentUrl = async (documentId, retryAttempt = 0) => {
	try {
		if (!documentId) {
			console.error("Document ID is required");
			throw new Error("Document ID is required");
		}

		// Validate document ID format - must be a non-empty string
		if (typeof documentId !== "string" || !documentId.trim()) {
			console.error("Document ID must be a valid string");
			throw new Error("Invalid document ID format");
		}
		// Clean and sanitize document ID (remove spaces, special characters if needed)
		const cleanDocId = documentId.trim().replace(/[\s\uFEFF\xA0]+/g, "");

		const headers = await getOpenSignAuthHeaders();
		if (!headers) {
			console.error(
				"OpenSign authentication failed, cannot get signing URL.",
			);
			throw new Error("OpenSign authentication failed");
		}
		console.log(`Getting signing URL for document ${documentId}`); // Enhanced URL parameters for better browser compatibility, specifically for Microsoft Edge
		const securityParams =
			"unsafe=false&trusted=true&embedded=true&secure=true&frame=allow&edge=1&security=trusted&override=1&x-frame-options=ALLOWALL&csp-bypass=true&ms-browser=edge&bypass-headers=true&allow-embedding=true&allow-same-origin=true&embedded-viewer=true";

		// Add additional browser detection for Edge-specific handling
		const isEdge = navigator.userAgent.indexOf("Edg") > -1;
		if (isEdge) {
			console.log(
				"Microsoft Edge detected, applying additional embedding optimizations",
			);
		}

		// Log the headers being sent to help with debugging
		console.log("OpenSign authentication headers:", {
			"X-Parse-Application-Id": headers["X-Parse-Application-Id"],
			"X-Parse-Session-Token": headers["X-Parse-Session-Token"]
				? "(set)"
				: "(not set)",
			SessionToken: headers["SessionToken"] ? "(set)" : "(not set)",
		});
		// Add additional headers that might help with authorization
		const enhancedHeaders = {
			...headers,
			"X-Parse-Master-Key": OPENSIGN_MASTER_KEY,
			// Removed 'Origin' and 'Referer' headers as browsers block them for security reasons
			// Removed 'Authorization' header as we're already using X-Parse-Session-Token
		};

		// Approach 2: Get document details first, then use URL in getsignedurl call
		try {
			console.log("Trying to get document details first"); // Create a clean copy of headers without potentially problematic headers
			const safeHeaders = {
				"Content-Type": enhancedHeaders["Content-Type"],
				"X-Parse-Application-Id":
					enhancedHeaders["X-Parse-Application-Id"],
				"X-Parse-Session-Token":
					enhancedHeaders["X-Parse-Session-Token"],
				"X-Parse-Master-Key": enhancedHeaders["X-Parse-Master-Key"],
			};

			const documentResponse = await axios.post(
				`${OPENSIGN_BASE_URL}/functions/getDocument`,
				{ docId: cleanDocId },
				{ headers: safeHeaders },
			);

			console.log("Document response:", documentResponse.data);

			if (!documentResponse.data || !documentResponse.data.result) {
				console.error(
					"Failed to retrieve document details:",
					documentResponse.data,
				);
				throw new Error("Could not retrieve document URL");
			}

			// Handle both possible response formats
			let documentDetails;
			if (Array.isArray(documentResponse.data.result)) {
				documentDetails = documentResponse.data.result[0];
			} else {
				documentDetails = documentResponse.data.result;
			}

			if (!documentDetails) {
				console.error(
					"Document details not found in response:",
					documentResponse.data,
				);
				throw new Error("Document details not found in response");
			}

			console.log("Document details:", documentDetails);

			// Check if the URL exists in the document
			const documentUrl = documentDetails.URL;
			if (!documentUrl) {
				console.error("Document has no URL property:", documentDetails);
				throw new Error("Document URL not found");
			}

			console.log(`Retrieved document URL: ${documentUrl}`); // Create a clean copy of headers without potentially problematic headers
			// Now call getsignedurl with all required parameters
			const fullResponse = await axios.post(
				`${OPENSIGN_BASE_URL}/functions/getsignedurl`,
				{
					url: documentUrl,
					docId: cleanDocId,
					_ApplicationId: OPENSIGN_APP_ID,
					isEmbedded: true,
					redirectUrl: window.location.origin + "/document/view",
				},
				{ headers: safeHeaders },
			);
			if (fullResponse.data && fullResponse.data.result) {
				console.log("Got signing URL successfully with full approach");
				let signedUrl =
					fullResponse.data.result.url || fullResponse.data.result;

				// Add security parameters for Microsoft Edge
				if (signedUrl) {
					const separator = signedUrl.includes("?") ? "&" : "?";
					signedUrl = `${signedUrl}${separator}${securityParams}`;
				}

				return signedUrl;
			}
		} catch (detailsError) {
			console.error(
				"Document details approach failed:",
				detailsError.message,
			);
			// Continue to fallback approach
		}
		// Approach 3: Fallback - try using the document ID as part of the URL path
		try {
			console.log(
				"Trying fallback approach with document ID in URL path",
			);

			// Create clean headers for the request
			const safeHeaders = {
				"Content-Type": enhancedHeaders["Content-Type"],
				"X-Parse-Application-Id":
					enhancedHeaders["X-Parse-Application-Id"],
				"X-Parse-Session-Token":
					enhancedHeaders["X-Parse-Session-Token"],
				"X-Parse-Master-Key": enhancedHeaders["X-Parse-Master-Key"],
			};
			const fallbackResponse = await axios.post(
				`${OPENSIGN_BASE_URL}/functions/getsignedurl`,
				{
					url: `${OPENSIGN_BASE_URL}/classes/contracts_Document/${cleanDocId}`,
					docId: cleanDocId,
					_ApplicationId: OPENSIGN_APP_ID,
					isEmbedded: true,
					redirectUrl: window.location.origin + "/document/view",
				},
				{ headers: safeHeaders },
			);
			if (fallbackResponse.data && fallbackResponse.data.result) {
				console.log(
					"Got signing URL successfully with fallback approach",
				);
				let signedUrl =
					fallbackResponse.data.result.url ||
					fallbackResponse.data.result;

				// Add security parameters for Microsoft Edge
				if (signedUrl) {
					const separator = signedUrl.includes("?") ? "&" : "?";
					signedUrl = `${signedUrl}${separator}${securityParams}`;
				}

				return signedUrl;
			}
		} catch (fallbackError) {
			console.error("Fallback approach failed:", fallbackError.message);
			// All approaches failed
		}

		// If we got here, all approaches failed
		throw new Error("All attempts to get signing URL failed");
	} catch (error) {
		console.error("Error getting signing URL:", error);

		// More detailed error logging
		if (error.response) {
			console.error("Response data:", error.response.data);
			console.error("Response status:", error.response.status);
			console.error("Response headers:", error.response.headers);
		} else if (error.request) {
			console.error(
				"Request was made but no response received:",
				error.request,
			);
		}

		// Retry logic for authentication issues
		if (
			error.response &&
			(error.response.status === 401 ||
				error.response.status === 403 ||
				error.response.status === 400) &&
			retryAttempt < 2
		) {
			console.log(
				`Retry attempt ${retryAttempt + 1} for document ${documentId}`,
			);

			// Force a new login attempt on first retry
			if (retryAttempt === 0) {
				console.log("Attempting to re-login due to error");
				openSignSessionToken = null;
				localStorage.removeItem("openSignSessionToken");
				await loginOpenSign();
			}

			// Standard retry with delay
			await new Promise((resolve) => setTimeout(resolve, 1000)); // 1s delay
			return getEmbeddedDocumentUrl(documentId, retryAttempt + 1);
		}

		throw error;
	}
};

export const checkSignatureStatus = async (documentId) => {
	try {
		const headers = await getOpenSignAuthHeaders();
		if (!headers) {
			console.error(
				"OpenSign authentication failed, cannot check signature status.",
			);
			throw new Error("OpenSign authentication failed");
		}

		console.log(`Checking signature status for document ${documentId}`);

		// Create a clean copy of headers without potentially problematic headers
		const safeHeaders = {
			"Content-Type": headers["Content-Type"],
			"X-Parse-Application-Id": headers["X-Parse-Application-Id"],
			"X-Parse-Session-Token": headers["X-Parse-Session-Token"],
			"X-Parse-Master-Key": headers["X-Parse-Master-Key"],
		};

		const response = await axios.post(
			`${OPENSIGN_BASE_URL}/functions/getDocument`,
			{
				docId: documentId,
			},
			{ headers: safeHeaders },
		);

		if (!response.data || !response.data.result) {
			console.error(
				"Invalid response structure from getDocument:",
				response.data,
			);
			throw new Error("Failed to check document status");
		}

		// Check document completion status
		const docStatus = response.data.result[0]?.IsCompleted
			? "completed"
			: "pending";
		console.log(`Document status: ${docStatus}`);
		return docStatus;
	} catch (error) {
		console.error(
			"Error checking signature status:",
			error.response ? error.response.data : error,
		);
		throw error;
	}
};

/**
 * Uploads a signed document back to the system
 * @param {Object} params - Upload parameters
 * @param {string} params.documentId - The OpenSign document ID
 * @param {string} params.assignmentName - The assignment name
 * @param {Blob} params.file - The signed PDF file blob
 * @returns {Promise<Object>} Upload response
 */
export const uploadSignedDocument = async ({
	documentId,
	assignmentName,
	file,
}) => {
	try {
		const formData = new FormData();
		formData.append("file", file, `${assignmentName}-signed.pdf`);
		formData.append("documentId", documentId);
		formData.append("assignmentName", assignmentName);

		const headers = await getOpenSignAuthHeaders();
		delete headers["Content-Type"]; // Let browser set content type for FormData

		const response = await axios.post(
			`${BACKEND_APP_API_URL}/api/uploadSignedDocument`,
			formData,
			{ headers },
		);
		return response.data;
	} catch (error) {
		console.error("Error uploading signed document:", error);
		throw error;
	}
};

/**
 * Sends a signing link to users for a document
 * @param {string} documentId - The OpenSign document ID
 * @param {string} assignmentName - The assignment name
 * @returns {Promise<Object>} Response from the sending operation
 */
export const sendSigningLink = async (documentId, assignmentName) => {
	try {
		const headers = await getOpenSignAuthHeaders();
		const response = await axios.post(
			`${BACKEND_APP_API_URL}/api/sendSigningLink`,
			{
				documentId,
				assignmentName,
			},
			{ headers },
		);
		return response.data;
	} catch (error) {
		console.error("Error sending signing link:", error);
		throw error;
	}
};

export const getOpenSignDocument = async (documentId, retryAttempt = 0) => {
	try {
		if (!documentId) {
			throw new Error("Document ID is required");
		}

		const headers = await getOpenSignAuthHeaders();
		if (!headers) {
			throw new Error("OpenSign authentication failed");
		}

		// Create a clean copy of headers without potentially problematic headers
		const safeHeaders = {
			"Content-Type": headers["Content-Type"],
			"X-Parse-Application-Id": headers["X-Parse-Application-Id"],
			"X-Parse-Session-Token": headers["X-Parse-Session-Token"],
			"X-Parse-Master-Key": headers["X-Parse-Master-Key"],
		};

		const response = await axios.post(
			`${OPENSIGN_BASE_URL}/functions/getDocument`,
			{ docId: documentId },
			{ headers: safeHeaders },
		);

		if (!response.data || !response.data.result) {
			throw new Error("Could not retrieve document details");
		}

		// Handle both possible response formats
		let documentDetails;
		if (Array.isArray(response.data.result)) {
			documentDetails = response.data.result[0];
		} else {
			documentDetails = response.data.result;
		}

		if (!documentDetails) {
			throw new Error("Document details not found in response");
		}

		return documentDetails;
	} catch (error) {
		// Retry logic for token expiry
		if (
			error.response &&
			(error.response.status === 401 || error.response.status === 403) &&
			retryAttempt < 1
		) {
			const errorData = error.response.data;
			if (
				errorData &&
				(errorData.error === "Invalid session token" ||
					errorData.code === 209 ||
					errorData.code === 141)
			) {
				openSignSessionToken = null;
				localStorage.removeItem("openSignSessionToken");
				await loginOpenSign();
				return getOpenSignDocument(documentId, retryAttempt + 1);
			}
		}

		throw error;
	}
};

/**
 * Tries to "wake up" the OpenSign server with a lightweight request
 * This can help ensure the server is responsive before embedding documents
 * @returns {Promise<boolean>} True if successful
 */
export const pingOpenSignServer = async () => {
	try {
		if (!OPENSIGN_BASE_URL) {
			console.error("OpenSign base URL is not defined");
			return false;
		}

		console.log("Pinging OpenSign server to ensure it's responsive...");

		// Make a lightweight request to the server
		const response = await fetch(`${OPENSIGN_BASE_URL}/health`, {
			method: "GET",
			headers: {
				"X-Parse-Application-Id": OPENSIGN_APP_ID || "",
				"Cache-Control": "no-cache",
			},
			mode: "cors",
			cache: "no-cache",
			timeout: 3000, // 3 second timeout
		});

		if (response.ok) {
			console.log("OpenSign server is responsive");
			return true;
		} else {
			console.warn(
				`OpenSign server responded with status: ${response.status}`,
			);
			// Try a fallback request to any endpoint that doesn't require authentication
			const fallbackResponse = await fetch(OPENSIGN_BASE_URL, {
				method: "GET",
				cache: "no-cache",
				timeout: 3000,
			});

			console.log(`Fallback ping response: ${fallbackResponse.status}`);
			return fallbackResponse.ok;
		}
	} catch (error) {
		console.warn("Failed to ping OpenSign server:", error);
		return false;
	}
};

/**
 * Get the list of signers for a document
 * @param {string} documentId - The OpenSign document ID
 * @returns {Promise<Array>} Array of signer objects with IDs
 */
export const getOpenSignDocumentSigners = async (
	documentId,
	retryAttempt = 0,
) => {
	try {
		if (!documentId) {
			throw new Error("Document ID is required");
		}

		const headers = await getOpenSignAuthHeaders();
		const response = await axios.get(
			`${OPENSIGN_BASE_URL}/functions/getSigners`,
			{
				headers,
				params: { documentId },
			},
		);

		if (response.data && response.data.result) {
			return response.data.result.signers || [];
		}
		return [];
	} catch (error) {
		if (error.response?.status === 401 && retryAttempt < 3) {
			// Token might be expired - clear it and retry
			openSignSessionToken = null;
			return getOpenSignDocumentSigners(documentId, retryAttempt + 1);
		}
		console.error("Error getting document signers:", error);
		throw error;
	}
};

/**
 * Get URL for a specific signer to sign a document
 * @param {string} documentId - The OpenSign document ID
 * @param {string} signerId - The signer's ID
 * @returns {Promise<string>} The signing URL
 */
export const getOpenSignDocumentUrl = async (documentId, signerId) => {
	if (!documentId || !signerId) {
		throw new Error("Document ID and Signer ID are required");
	}

	// Value sign URL format construction
	const baseUrl =
		process.env.REACT_APP_OPENSIGN_URL || process.env.ValueSignURL;
	if (!baseUrl) {
		throw new Error("OpenSign URL is not configured");
	}

	return `${baseUrl}/load/recipientSignPdf/${documentId}/${signerId}`;
};

/**
 * Updates an OpenSign document to finalize it from draft state to active/signable state
 * @param {string} documentId - The OpenSign document ID
 * @param {Object} documentData - The document data to update, including placeholders, signers, etc.
 * @returns {Promise<Object>} The updated document
 */
export const finalizeOpenSignDocument = async (documentId, documentData) => {
	try {
		if (!documentId) {
			throw new Error("Document ID is required");
		}

		const headers = await getOpenSignAuthHeaders();
		if (!headers) {
			throw new Error("OpenSign authentication failed");
		}

		// Create a clean copy of headers
		const safeHeaders = {
			"Content-Type": "application/json",
			"X-Parse-Application-Id": headers["X-Parse-Application-Id"],
			"X-Parse-Session-Token": headers["X-Parse-Session-Token"],
			"X-Parse-Master-Key": headers["X-Parse-Master-Key"],
		};

		// Create a minimal document object with just the essential fields if one wasn't provided
		const finalDocData = documentData || {
			Status: "active",
			SentToOthers: true,
			Stage: "review",
		};

		// Always add a fresh ExpiryDate to ensure it's properly formatted
		const expiryDate = new Date();
		expiryDate.setDate(expiryDate.getDate() + 30);
		finalDocData.ExpiryDate = {
			__type: "Date",
			iso: expiryDate.toISOString(),
		};

		// Ensure document status is active
		finalDocData.Status = "active";
		finalDocData.SentToOthers = true;

		const response = await axios.put(
			`${OPENSIGN_BASE_URL}/classes/contracts_Document/${documentId}`,
			finalDocData,
			{ headers: safeHeaders },
		);

		if (!response.data) {
			throw new Error("Failed to update document status");
		}

		return response.data;
	} catch (error) {
		console.error("Error finalizing document:", error);
		if (error.response) {
			console.error("Response data:", error.response.data);
			console.error("Response status:", error.response.status);
		}
		// For 404 errors, the document may have been deleted or the objectId is incorrect.
		// Don't throw - return a fallback so the signing flow can still proceed.
		if (error.response && error.response.status === 404) {
			console.warn(
				"Document not found during finalization (404). The document may already be in the correct state or was created with different permissions. Proceeding without finalization update.",
			);
			return { warning: "Document not found for finalization update" };
		}
		throw error;
	}
};
